import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { AlertTriangle, File, Mail, Trash } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../../Shared/api';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { ConfirmButton } from '../../../Shared/UI/ConfirmButton';
import Delimit from '../../../Shared/UI/Delimit';
import { Icon } from '../../../Shared/UI/Icon';
import { useRouter } from '../../router';
import { Donation, DonationForm, SharedProps, Subscription } from '../../types';
import Container from '../../UI/Container';
import Layout from '../../UI/Layout';
import EditSubscriptionForm from './EditSubscriptionForm';
import { SubscriptionLayout } from './SubscriptionLayout';
import SubscriptionStatusLabel from './SubscriptionStatusLabel';

export interface SubscriptionPageProps extends SharedProps {
  subscription: Subscription & {
    donation_form: DonationForm;
    latest_donation: Donation | null;
    donations_count: number;
    next_payment_date: string | null;
    revenue: number;
    deletable: boolean;
  };
  donationForms: DonationForm[];
}

export default function SubscriptionPage({
  subscription,
  donationForms,
  charity,
}: SubscriptionPageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();
  const { formatNumber, formatCurrency, formatDate } = useLocale();

  const [
    sendSubscriptionConfirmationMail,
    { loading: sendingConfirmationMail },
  ] = useMutation(routes.send_subcription_confirmation_mail(subscription.id), {
    onSuccess: () => {
      notifier.notify(t('backend:mail_sent'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  const [
    sendSubscriptionCancellationMail,
    { loading: sendingCancellationMail },
  ] = useMutation(routes.send_subcription_cancellation_mail(subscription.id), {
    onSuccess: () => {
      notifier.notify(t('backend:mail_sent'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  const [deleteSubscription, { loading: deleting }] = useMutation(routes.delete_subscription(subscription.id), {
    onSuccess: () => {
      notifier.notify(t('backend:donor_deleted'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  return (
    <Container>
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-3 space-y-4 text-slate-600" style={{ minWidth: 0 }}>
          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:donation_form')}
            </h4>
            <Link
              href={routes.donation_form_page(subscription.donation_form.id)}
              className={classNames({ 'opacity-50': !subscription.donation_form.enabled })}
            >
              <Icon className="mr-1">
                <File />
              </Icon>
              {subscription.donation_form.internal_title}
            </Link>
          </div>

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:status')}
            </h4>
            <SubscriptionStatusLabel active={subscription.active} />
          </div>

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:payments')}
            </h4>
            {formatNumber(subscription.donations_count)}
          </div>

          {subscription.latest_donation && (
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:latest_payment')}
              </h4>
              <Delimit>
                {formatCurrency(subscription.latest_donation.amount)}
                {formatDate(subscription.latest_donation.created_at)}
              </Delimit>
            </div>
          )}

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:next_collection')}
            </h4>
            <div className={classNames(!subscription.next_payment_date && 'text-slate-400')}>
              {subscription.next_payment_date ? formatDate(subscription.next_payment_date) : '–'}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:start_date')}
            </h4>
            <Delimit>
              {formatDate(subscription.start_date)}
            </Delimit>
          </div>

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:revenue')}
            </h4>
            {formatCurrency(subscription.revenue)}
          </div>

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:mandate_reference')}
            </h4>
            {subscription.mandate_reference}
          </div>

          {subscription.active && (
            <div>
              <ConfirmButton
                onClick={() => sendSubscriptionConfirmationMail()}
                loading={sendingConfirmationMail}
                variant="secondary"
                size="sm"
              >
                <Icon className="mr-1">
                  <Mail />
                </Icon>
                {t('backend:send_confirmation_mail')}
              </ConfirmButton>
            </div>
          )}

          {subscription.end_date && (
            <div>
              <ConfirmButton
                onClick={() => sendSubscriptionCancellationMail()}
                loading={sendingCancellationMail}
                variant="secondary"
                size="sm"
              >
                <Icon className="mr-1">
                  <Mail />
                </Icon>
                {t('backend:send_cancellation_mail')}
              </ConfirmButton>
            </div>
          )}

          {subscription.deletable && (
            <div>
              <ConfirmButton
                onClick={() => deleteSubscription()}
                loading={deleting}
                variant="danger"
                size="sm"
              >
                <Icon className="mr-1">
                  <Trash />
                </Icon>
                {t('backend:delete_recurring_donation')}
              </ConfirmButton>
            </div>
          )}
        </div>

        <div className="col-span-9">
          <EditSubscriptionForm
            subscription={subscription}
            donationForms={donationForms}
            charity={charity}
          />
        </div>
      </div>
    </Container>
  );
}

SubscriptionPage.layout = (page: ReactNode) => <Layout><SubscriptionLayout>{page}</SubscriptionLayout></Layout>;
